import * as THREE from 'three';
import { SKETCH_COLORS, SKETCH_MATERIALS, sketchMaterialColor, SKETCH_STYLE_COLORS } from '../../themes/colors';
import type { SketchDisplayStyle } from '../../core/sketch/types';
import { BACKFACE_DEFINES } from '../backfaceTint';

/**
 * Face material resolution per display style.
 *
 * Style language (BIM viewMode parity):
 * - wireframe:        faces fully transparent, edges carry the shape
 * - hiddenLine:       white faces + strong dark edges (reading of a drawing)
 * - shaded:           palette material colors, matte standard lighting
 * - consistentColors: unlit flat material colors (no light response)
 * - realistic:        glossier PBR response under the sun light, no edge overlay
 * Legacy sketch styles (monochrome, xray) stay valid for saved scenes.
 */

/**
 * Backface shading — the SketchUp backface language. Reversed faces
 * (interiors, tunnel walls seen from inside) read as a fixed DARKER tone
 * while KEEPING the scene's shading: the tint swaps the back side's diffuse
 * color BEFORE lighting, so sun direction, shadows and the ambient grade
 * stay visible on it — a back face reads as the same surface from behind,
 * never as a black hole. Opt-in via the shared USE_BACKFACE_TINT define;
 * the chunk patch itself lives app-wide in rendering/backfaceTint, so every
 * workbench speaks the same language. A face seen from its back side still
 * raycasts as one mesh (face picking from any side).
 */
export function applyBackfaceTint(
    material: THREE.MeshStandardMaterial | THREE.MeshBasicMaterial
): void {
    material.defines = { ...material.defines, ...BACKFACE_DEFINES };
}

export interface FaceMaterialProps {
    color: string;
    opacity: number;
    transparent: boolean;
    depthWrite: boolean;
    /** True → unlit flat color (meshBasicMaterial), like BIM's Consistent Colors. */
    flat: boolean;
    /** PBR surface response for lit styles (Realistic gloss vs matte Shaded). */
    roughness: number;
    metalness: number;
    /** Edge line color paired with this face style. */
    edgeColor: string;
    edgeOpacity: number;
    /** Backface shading on reversed faces (off for drawing-style reads). */
    backfaceTint: boolean;
}

export function faceMaterialProps(
    style: SketchDisplayStyle,
    materialId: string | undefined,
    fallbackMaterialId: string
): FaceMaterialProps {
    switch (style) {
        case 'hiddenLine':
            // Technical-drawing read: every face white, no backface language.
            return { color: SKETCH_STYLE_COLORS.hiddenFace, opacity: 1, transparent: false, depthWrite: true, flat: false, roughness: 1, metalness: 0, edgeColor: SKETCH_STYLE_COLORS.hiddenEdge, edgeOpacity: 1, backfaceTint: false };
        case 'monochrome':
            return { color: SKETCH_STYLE_COLORS.monoFace, opacity: 1, transparent: false, depthWrite: true, flat: false, roughness: 1, metalness: 0, edgeColor: SKETCH_STYLE_COLORS.monoEdge, edgeOpacity: 1, backfaceTint: true };
        case 'wireframe':
            // Keep the mesh present (it must keep catching raycasts) but
            // fully see-through with no depth contribution.
            return { color: SKETCH_STYLE_COLORS.hiddenEdge, opacity: 0, transparent: true, depthWrite: false, flat: false, roughness: 1, metalness: 0, edgeColor: SKETCH_STYLE_COLORS.wireEdge, edgeOpacity: 1, backfaceTint: false };
        case 'xray':
            return { color: sketchMaterialColor(materialId ?? fallbackMaterialId), opacity: 0.35, transparent: true, depthWrite: false, flat: false, roughness: 1, metalness: 0, edgeColor: SKETCH_STYLE_COLORS.wireEdge, edgeOpacity: 0.9, backfaceTint: true };
        case 'consistentColors':
            return { color: sketchMaterialColor(materialId ?? fallbackMaterialId), opacity: 1, transparent: false, depthWrite: true, flat: true, roughness: 1, metalness: 0, edgeColor: SKETCH_STYLE_COLORS.shadedEdge, edgeOpacity: 1, backfaceTint: true };
        case 'realistic':
            // BIM parity: Realistic drops the edge-family overlay; the sun
            // light alone carries the form via the glossier PBR surface.
            return { color: sketchMaterialColor(materialId ?? fallbackMaterialId), opacity: 1, transparent: false, depthWrite: true, flat: false, roughness: 0.35, metalness: 0.05, edgeColor: SKETCH_STYLE_COLORS.shadedEdge, edgeOpacity: 0, backfaceTint: true };
        case 'shaded':
        default:
            return { color: sketchMaterialColor(materialId ?? fallbackMaterialId), opacity: 1, transparent: false, depthWrite: true, flat: false, roughness: 1, metalness: 0, edgeColor: SKETCH_STYLE_COLORS.shadedEdge, edgeOpacity: 1, backfaceTint: true };
    }
}

/**
 * Cross-workbench style mapping. The sketch display style and the BIM
 * view-mode enum describe the same visual language (see faceMaterialProps),
 * so the shared renderers need one translation in each direction:
 * - sketchStyleToViewMode: the Sketch workbench shades the BIM content layer
 *   with its own display style.
 * - viewModeToSketchStyle: the 3D/BIM workbenches shade the sketch-solid
 *   reference layer with the shared app view mode.
 * Legacy sketch styles (monochrome, xray) fold onto their BIM equivalents;
 * BIM-only modes (rendered, ghosted) fold onto the closest sketch style.
 */
export function sketchStyleToViewMode(
    style: SketchDisplayStyle
): 'wireframe' | 'shaded' | 'rendered' | 'xray' | 'hiddenLine' | 'consistentColors' | 'realistic' {
    switch (style) {
        case 'wireframe': return 'wireframe';
        case 'hiddenLine':
        case 'monochrome': return 'hiddenLine';
        case 'xray': return 'xray';
        case 'consistentColors': return 'consistentColors';
        case 'realistic': return 'realistic';
        case 'shaded':
        default: return 'shaded';
    }
}

export function viewModeToSketchStyle(viewMode: string): SketchDisplayStyle {
    switch (viewMode) {
        case 'wireframe': return 'wireframe';
        case 'hiddenLine': return 'hiddenLine';
        case 'xray':
        case 'ghosted': return 'xray';
        case 'consistentColors': return 'consistentColors';
        case 'realistic': return 'realistic';
        case 'shaded':
        case 'rendered':
        default: return 'shaded';
    }
}

export { SKETCH_COLORS, SKETCH_MATERIALS, sketchMaterialColor };
